import React, { useContext, useMemo } from "react";
import { FormattedMessage } from "react-intl-phraseapp";
import { HashLink } from "react-router-hash-link";
import {
  DrugCostInfo,
  PharmacyInfo,
  Plan,
  PlanDetailsPageSection,
} from "../../../@types";
import { CaptionProp } from "../../../@types/react-table";
import { AppStore } from "../../../app/store";
import {
  Footnote,
  Footnotes,
  FootnoteMarker,
  HelpDrawerWrapper,
  RenderedTable,
} from "../../../components";
import {
  sendTealiumEvent,
  useTranslate,
  addRowAttributes,
  makePlanLongId,
  planHasDrugCoverage,
} from "../../../helpers";
import { createGa4Event, Ga4Event } from "../../../helpers/analytics";
import { CellValue, TableInstance } from "react-table";
import { useLocation } from "react-router";
import { Routes } from "../../../app";
import { hasMailOrderPharmacy } from "../../../app/store/AppStore";
import { useGetPharmacies } from "./drugCoverageHelpers";
import { PharmacyNetworkStatusStackedHeading } from "../components";
import { Link } from "react-router-dom";
import classNames from "classnames";
import messages from "../../../translations/en-US.json";
import { useFlags } from "launchdarkly-react-client-sdk";
import { newBasicHelperDrawerAnalytics } from "../../../components/BasicHelperDrawerAnalytics";

export const retailInNetwork = `retail-in-network`;
export const retailOutOfNetwork = `retail-out-of-network`;
export const mailOrderNotCovered = `mail-order-not-covered`;
export const mailOrderCovered = `mail-order-info`;

export const mailOrderNotCoveredPharmacy: PharmacyInfo = {
  in_network: false,
  preferred: false,
  mail_order: true,
  name: messages["plan_details.pharmacies.mail_order_pharmacy"],
  npi: "mail-order",
  // ltc flag does not apply to mail order pharmacies.
  ltc: false,
};

export const PharmaciesTable: React.FC<{
  drugCostInfo: DrugCostInfo;
  plan: Plan;
}> = ({ drugCostInfo, plan }) => {
  const flags = useFlags();
  const { costs: pharmacyCosts } = drugCostInfo;
  const t = useTranslate();
  const location = useLocation();
  const { state } = useContext(AppStore.AppContext);
  const pharmacies = useGetPharmacies(drugCostInfo);
  const includesMailOrder = hasMailOrderPharmacy(state);
  const showMailOrderNotCovered =
    !pharmacies.some(p => p.mail_order) && includesMailOrder;
  const hasDrugCoverage = planHasDrugCoverage(plan);
  const planLongId = makePlanLongId(drugCostInfo.plan);

  /**
   * Set table row attributes for testing, based on data and logic about
   * retail and mail order pharmacies, and the specified plan
   * (RenderTable adds as props)
   * Passed to the `react-table` Cell function.
   * Adds row attributes and returns the original value of the cell contents
   * @param params
   * @param params.value - A table cell value
   * @param params.row - A table row instance
   * @return - The original value of the cell
   */
  const setRowDataTestIds = ({
    value,
    row,
  }: Partial<TableInstance> | CellValue) => {
    // If there is a pharmacy defined for this row, get the in_network and
    // mail_order booleans, defaulting both to null
    const { in_network = null, mail_order = null } = pharmacies[row.id] || {};
    let dataTestId;
    // If the pharmacy for this row is mail_order and pharmacyType
    // includes mail order, set to mailOrderCovered
    if (null !== mail_order && mail_order && includesMailOrder) {
      dataTestId = mailOrderCovered;
    }
    // If the in_network boolean is set, add a retail pharmacy test id for
    // in-network or out-of-network
    else if (null !== in_network) {
      dataTestId = in_network ? retailInNetwork : retailOutOfNetwork;
    } else if (showMailOrderNotCovered) {
      dataTestId = mailOrderNotCovered;
    }
    // If we have a dataTestId, add it to `row.attributes`
    if (dataTestId) {
      addRowAttributes(row, { ["data-testid"]: dataTestId });
    }
    return value;
  };

  const displayLTCFootnote = (pharmacies: PharmacyInfo[]): boolean => {
    return pharmacies.some(pharmacy => pharmacy.ltc === true);
  };

  const LTC_FOOTNOTE_KEY = "ltcfootnote-1";

  const ltcFootnote = (
    <Footnotes>
      <Footnote
        id={LTC_FOOTNOTE_KEY}
        className="ds-u-margin--0"
        text={
          <FormattedMessage
            id="plan_details.pharmacies.ltc_footnote"
            values={{
              link: (
                <HashLink to="#plan-contact">
                  {t("plan_details.pharmacies.ltc_footnote.contact_the_plan")}
                </HashLink>
              ),
            }}
          />
        }
        marker={"1"}
        key={LTC_FOOTNOTE_KEY}
      />
    </Footnotes>
  );

  const ltcFootnoteMarker = (
    <FootnoteMarker key={LTC_FOOTNOTE_KEY} id={LTC_FOOTNOTE_KEY} marker={"1"} />
  );

  const getPharmacyNameWithFootnote = (pharmacy: PharmacyInfo): JSX.Element => {
    return (
      <span className="mct-c-table__header-title">
        {pharmacy.name}
        {pharmacy.ltc && ltcFootnoteMarker}
      </span>
    );
  };

  // Table columns and data
  const accessors = {
    pharmacyName: "pharmacyName",
    pharmacyDetails: "pharmacyDetails",
  };
  const pharmacyCols = useMemo(
    () =>
      Object.keys(accessors).map((key, i) => {
        const config = {
          accessor: accessors[key],
          className: i === 1 && "mct-c-plan-details__pharmacies-network-status",
          Cell: setRowDataTestIds,
          Footer: i === 0 && displayLTCFootnote(pharmacies) && ltcFootnote,
        };
        return config;
      }),
    [pharmacyCosts, state.language]
  );

  mailOrderNotCoveredPharmacy.name = t(
    "plan_details.pharmacies.mail_order_pharmacy"
  );

  const pharmacyData = useMemo(() => {
    const pharmaciesArray = pharmacies.map(pharmacy => {
      return {
        [accessors.pharmacyName]: getPharmacyNameWithFootnote(pharmacy),
        [accessors.pharmacyDetails]: (
          <PharmacyNetworkStatusStackedHeading
            drugCostInfo={drugCostInfo}
            npi={pharmacy.npi}
            location={location}
            planLongId={planLongId}
            hasDrugCoverage={hasDrugCoverage}
            showCostsVaryForMO={pharmacy.mail_order && includesMailOrder}
          />
        ),
      };
    });
    // If `showMailOrderNotCovered` is true, pharmacies gotten from `drugCostInfo`
    // do not include the MO pharmacy, so it has to be added manually, and the
    // `PharmacyNetworkStatus` takes an override object (plus extra boolean props)
    // that makes it show the correct messaging.
    showMailOrderNotCovered &&
      pharmaciesArray.push({
        [accessors.pharmacyName]: getPharmacyNameWithFootnote(
          mailOrderNotCoveredPharmacy
        ),
        [accessors.pharmacyDetails]: (
          <PharmacyNetworkStatusStackedHeading
            pharmacy={mailOrderNotCoveredPharmacy}
            showCostsVaryForMO={true}
            mailOrderNotCovered={true}
          />
        ),
      });
    return pharmaciesArray;
  }, [pharmacyCosts, t]);

  const changePharmaciesUrl = {
    pathname: `${Routes.inNetworkPharmacy.replace(":id", planLongId)}`,
    state: {
      nextRoute: {
        ...location,
        hash: PlanDetailsPageSection.DRUG_COVERAGE,
      },
    },
  };

  const helpDrawerAnalytics = newBasicHelperDrawerAnalytics({
    clickText: "More about pharmacy cost levels",
    state,
    flags,
  });

  const caption: CaptionProp = {
    title: t("plan_details.pharmacies"),
    // This verbosity is necessary to avoid displayName errors thrown with an
    // anonymous function method that returns JSX. TBD other solutions
    render: function renderTableCaption(title: JSX.Element) {
      return (
        <div className="ds-u-display--flex ds-u-justify-content--between ds-u-align-items--center ds-u-flex-wrap--wrap ds-u-lg-flex-wrap--nowrap">
          <div
            className={classNames({
              "mct-c-table__caption-col--1": hasDrugCoverage,
            })}
          >
            {title}
            <p>{t("plan_card.pharmacies.description")}</p>
            <HelpDrawerWrapper
              toggleClassName="ds-u-margin-bottom--3 ds-u-lg-margin-bottom--0 mct-hide-print"
              toggleText={t(
                "plan_card.pharmacies.more_about_pharmacy_cost_levels"
              )}
              drawerTitle={t(
                "plan_card.pharmacies.more_about_pharmacy_cost_levels"
              )}
              mctAnalytics={helpDrawerAnalytics}
            >
              <p>{t("plan_card.pharmacies.help_drawer.intro_text")}</p>
              <ul>
                <li>
                  {t("plan_card.pharmacies.help_drawer.preferred_in_network")}
                </li>
                <li>
                  {t("plan_card.pharmacies.help_drawer.standard_in_network")}
                </li>
                <li>{t("plan_card.pharmacies.help_drawer.out_of_network")}</li>
                <li>{t("plan_card.pharmacies.help_drawer.mail_order")}</li>
              </ul>
            </HelpDrawerWrapper>
          </div>
          {hasDrugCoverage && (
            <div className="ds-u-display--flex ds-u-lg-justify-content--end mct-u-flex-shrink--0 mct-c-table__caption-col--2">
              <Link
                className="ds-c-button mct-u-flex-shrink--0 mct-hide-print"
                to={changePharmaciesUrl}
                onClick={() => {
                  sendTealiumEvent("Plan Details", "Change Pharmacies");
                  sendTealiumEvent(
                    "Find Plans - Add Pharmacy",
                    "View pharmacy search results"
                  );
                  createGa4Event({
                    event_name: Ga4Event.PHARMACY_SEARCH_RESULTS,
                    click_text: "Change Pharmacies",
                    state,
                    flags,
                  });
                }}
              >
                {t("plan_details.pharmacies.change_pharmacies")}
              </Link>
            </div>
          )}
        </div>
      );
    },
  };

  // @TODO - Need to pass some other props? E.g. sectionId?
  // @see: https://github.cms.gov/CMS-MCT/coverage-tools-frontend/blob/3fcdac6b56ed166db594eb45c183bda1c721633d/src/pages/PlanDetails2020/Pharmacies.tsx#L108

  return (
    <RenderedTable
      tableColumns={pharmacyCols}
      tableData={pharmacyData}
      rowHeaders // Indicates the first column should be rendered as `th scope="row"`
      borderless // is a default for Plan Details, TBD if it should be baked in
      stackableBreakpoint="lg" // is a default for Plan Details, TBD if it should be baked in
      caption={caption}
      className="PharmaciesTable"
    />
  );
};
